import { useEffect, useRef, useState } from 'react';

/** How long a sheet that closed keeps its opener for one that opens after it. */
const HAND_OFF_MS = 1000;

/**
 * The opener the last sheet using this hook left as it closed, for the sheet that follows it. One
 * sheet often opens as another closes: a queued celebration is granted in the dismiss that ends the
 * one before it, and a step celebration follows the activation drawer it was earned in. The next
 * sheet's own read then finds a control inside the sheet going away, or nothing, never the control
 * that started the chain. Taken by the next sheet that mounts, and let go HAND_OFF_MS after it was
 * left, so it never holds a page's DOM past that: in each chain today the next sheet mounts within
 * the task that closed the last one.
 */
let handOff: { opener: HTMLElement } | null = null;

/** Every overlay primitive portals into #overlay-root (index.html; overlay-system-design §4). */
const isInOverlay = (element: Element): boolean => element.closest('#overlay-root') !== null;

/** Inside a sheet that stays open: Radix marks its dialog `data-state="open"` until it closes. */
const isInOpenSheet = (element: Element): boolean =>
  element.closest('[role="dialog"]')?.getAttribute('data-state') === 'open';

/**
 * Focus return for a sheet with no Trigger. Radix gives focus back to a dialog's Trigger when it
 * closes; a sheet opened from code has none, so focus fell to <body>, and keyboard and
 * screen-reader users started again from the top of the page (WAI dialog pattern: focus goes back
 * to the control that opened it).
 *
 * The opener is the element focused when the component first renders, read during render: before
 * the commit in which a child's autoFocus can move focus into the sheet. That fits a sheet that
 * mounts when it opens and unmounts when it closes, as the celebrations do. Not one that stays
 * mounted and toggles `open`: this reads at mount, so focus would go back to whatever was focused
 * then.
 *
 * In a chain that read finds the sheet before it (a control in #overlay-root, removed as that sheet
 * unmounts) or <body>, so the sheet takes the opener the one before it left: focus goes back, at
 * the end of the chain, to the control that started it. A sheet whose read is a control on the page
 * keeps it, and so does one opened from a sheet that stays open under it.
 *
 * Pass the result as the sheet's `onCloseAutoFocus`. Best effort: the opener must still be on the
 * page. If another sheet holds focus by the time this one's primitive asks (the next one in a
 * chain), focus stays there. That needs the next sheet to take focus as it opens: the celebration
 * frame does (onto its sheet), the PIN sheet through its input and the video dialog through Radix's
 * own autofocus. A plain vaul drawer does not (vaul turns it off), and none follows a sheet today.
 */
export function useReturnFocusOnClose(): (event: Event) => void {
  const [read] = useState(() => (typeof document === 'undefined' ? null : document.activeElement));
  const openerRef = useRef<HTMLElement | null>(null);

  // After the commit, so the sheet this one replaced has unmounted and left its opener: React runs
  // every effect cleanup before any effect, even for a sheet replaced in the same commit.
  useEffect(() => {
    const stillThere =
      read instanceof HTMLElement && read !== document.body && read.isConnected ? read : null;
    const left = handOff?.opener.isConnected ? handOff.opener : null;
    handOff = null;
    // Nothing, or a control in a sheet on its way out: this one follows that sheet, so it takes the
    // opener left behind, if any. A sheet opened from one that stays open keeps its read.
    const follows = stillThere === null || (isInOverlay(stillThere) && !isInOpenSheet(stillThere));
    const opener = (follows ? left : null) ?? stillThere;
    openerRef.current = opener;
    return () => {
      if (!opener) return;
      const leaving = { opener };
      handOff = leaving;
      setTimeout(() => {
        if (handOff === leaving) handOff = null;
      }, HAND_OFF_MS);
    };
  }, [read]);

  return event => {
    event.preventDefault();
    const active = document.activeElement;
    if (active && isInOverlay(active)) return;
    const opener = openerRef.current;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };
}
